import { useQuery } from '@vue/apollo-composable';
import { computed, type ComputedRef } from 'vue';

import { getCountryCode } from '@/shared/utils/country';
import {
  shopifyGidToId,
  shopifyIdToGid
} from '@/shared/utils/shopify/id-formatter';

import { kitItemsQuery } from './useKitsStorefront.queries';

export type KitItemInput = {
  variantId: string;
  quantity: number;
};

export type ResolvedKitItem = {
  quantity: number;
  title: string;
  variantTitle?: string;
  image?: string;
  availableForSale: boolean;
};

type VariantNode = {
  __typename: 'ProductVariant';
  id: string;
  title?: string | null;
  availableForSale?: boolean | null;
  image?: { url?: string | null } | null;
  product?: { title?: string | null } | null;
};

function isVariantNode(node: unknown): node is VariantNode {
  if (typeof node !== 'object' || node === null) return false;
  if (!('__typename' in node) || !('id' in node)) return false;
  return node.__typename === 'ProductVariant' && typeof node.id === 'string';
}

export const useKits = (items: ComputedRef<KitItemInput[]>) => {
  const variantGids = computed(() =>
    items.value.map((item) => shopifyIdToGid(item.variantId, 'ProductVariant'))
  );

  const { result, loading } = useQuery(
    kitItemsQuery,
    computed(() => ({
      ids: variantGids.value,
      country: getCountryCode()
    })),
    {
      clientId: 'storefront',
      enabled: computed(() => items.value.length > 0)
    }
  );

  const nodeMap = computed(() => {
    const map = new Map<string, VariantNode>();
    for (const node of result.value?.nodes ?? []) {
      if (isVariantNode(node)) {
        map.set(shopifyGidToId(node.id), node);
      }
    }
    return map;
  });

  const resolvedItems = computed((): ResolvedKitItem[] =>
    items.value.map((item) => {
      const node = nodeMap.value.get(item.variantId);
      return {
        quantity: item.quantity,
        title: node?.product?.title ?? '',
        variantTitle: node?.title ?? undefined,
        image: node?.image?.url ?? undefined,
        availableForSale: node?.availableForSale === true
      };
    })
  );

  return { resolvedItems, loading };
};
